import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';
import EmptyState from 'components/EmptyState';
import BatchTimelineModal from 'components/BatchTimelineModal';

const DATE_RANGES = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: 'Todo', days: null },
];

const COLUMNS = [
  { key: 'batch_ref', label: 'Lote', sortable: false },
  { key: 'sold_at', label: 'Fecha', sortable: true },
  { key: 'metal_name', label: 'Metal', sortable: true },
  { key: 'category_name', label: 'Categoría', sortable: true },
  { key: 'weight_t', label: 'Peso (t)', sortable: true },
  { key: 'entry_price', label: 'Entrada €/t', sortable: true },
  { key: 'sale_price_per_ton', label: 'Venta €/t', sortable: true },
  { key: 'margin_per_ton', label: 'Margen €/t', sortable: true },
  { key: 'realized_pnl', label: 'Margen Total €', sortable: true },
  { key: 'yield_delta', label: 'Yield Est/Real %', sortable: true },
  { key: 'days_in_inventory', label: 'Días Inv.', sortable: true },
  { key: 'buyer_name', label: 'Comprador', sortable: true },
  { key: 'status', label: 'Estado', sortable: false },
];

function getMarginColor(marginPct) {
  if (marginPct === null || marginPct === undefined) return 'text-muted-foreground';
  if (marginPct >= 12) return 'text-emerald-600';
  if (marginPct >= 8) return 'text-amber-600';
  return 'text-red-500';
}

function getMarginBg(marginPct) {
  if (marginPct === null || marginPct === undefined) return '';
  if (marginPct >= 12) return 'bg-emerald-50';
  if (marginPct >= 8) return 'bg-amber-50';
  return 'bg-red-50';
}

function exportCSV(rows) {
  const headers = ['Lote', 'Fecha', 'Metal', 'Categoría', 'Peso (t)', 'Entrada €/t', 'Venta €/t', 'Margen €/t', 'Margen Total €', 'Yield Est %', 'Yield Real %', 'Días Inv.', 'Comprador'];
  const lines = rows?.map(r => [
    r?.batch_name || r?.batch_id || '',
    r?.sold_at ? new Date(r?.sold_at)?.toLocaleDateString('es-ES') : '',
    r?.metal_name || '',
    r?.category_name || '',
    r?.weight_t || '',
    r?.entry_price || '',
    r?.sale_price_per_ton || '',
    r?.margin_per_ton?.toFixed(2) || '',
    r?.realized_pnl?.toFixed(2) || '',
    r?.yield_est || '',
    r?.actual_yield_pct || '',
    r?.days_in_inventory || '',
    r?.buyer_name || '',
  ]);
  const csv = [headers, ...lines]?.map(row => row?.join(';'))?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL?.createObjectURL(blob);
  const a = document?.createElement('a');
  a.href = url;
  a.download = `ventas_${new Date()?.toISOString()?.slice(0, 10)}.csv`;
  a?.click();
  URL?.revokeObjectURL(url);
}

export default function SalesTable({ sales, loading, onNavigateInventory, externalFilters, onClearExternalFilter }) {
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(30);
  const [sortField, setSortField] = useState('sold_at');
  const [sortDir, setSortDir] = useState('desc');
  const [timelineModal, setTimelineModal] = useState(null); // { batchId }

  const enriched = useMemo(() => {
    return (sales || [])?.map(s => {
      const entryPrice = s?.purchase_value && s?.weight_t
        ? parseFloat(s?.purchase_value) / parseFloat(s?.weight_t)
        : 0;
      const marginPerTon = parseFloat(s?.sale_price_per_ton) - entryPrice;
      const marginPct = entryPrice > 0 ? (marginPerTon / entryPrice) * 100 : null;
      const yieldDelta = s?.actual_yield_pct && s?.yield_est
        ? parseFloat(s?.actual_yield_pct) - parseFloat(s?.yield_est)
        : null;
      return { ...s, entry_price: entryPrice, margin_per_ton: marginPerTon, margin_pct: marginPct, yield_delta: yieldDelta };
    });
  }, [sales]);

  const filtered = useMemo(() => {
    let list = enriched;
    // Apply external drill-down filters
    if (externalFilters?.metal) {
      list = list?.filter(s => s?.metal_name === externalFilters?.metal);
    }
    if (externalFilters?.category) {
      list = list?.filter(s => s?.category_name === externalFilters?.category);
    }
    if (externalFilters?.buyer) {
      list = list?.filter(s => s?.buyer_name === externalFilters?.buyer);
    }
    if (dateRange) {
      const cutoff = new Date(Date.now() - dateRange * 24 * 60 * 60 * 1000);
      list = list?.filter(s => s?.sold_at && new Date(s?.sold_at) >= cutoff);
    }
    if (search?.trim()) {
      const q = search?.toLowerCase();
      list = list?.filter(s =>
        s?.metal_name?.toLowerCase()?.includes(q) ||
        s?.category_name?.toLowerCase()?.includes(q) ||
        s?.buyer_name?.toLowerCase()?.includes(q)
      );
    }
    return list?.sort((a, b) => {
      const av = a?.[sortField];
      const bv = b?.[sortField];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const cmp = typeof av === 'string' ? av?.localeCompare(bv) : (av - bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [enriched, dateRange, search, sortField, sortDir, externalFilters]);

  const handleSort = (key) => {
    if (sortField === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(key); setSortDir('desc'); }
  };

  const SortIcon = ({ col }) => {
    if (!col?.sortable) return null;
    if (sortField !== col?.key) return <Icon name="ChevronsUpDown" size={12} color="var(--color-muted-foreground)" />;
    return <Icon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={12} color="var(--color-accent)" />;
  };

  const activeExternalFilters = Object.entries(externalFilters || {})?.filter(([, v]) => !!v);

  const getEmptyState = () => {
    const hasSearch = search?.trim();
    const hasDateFilter = dateRange !== null;
    const totalSales = (sales || [])?.length;

    if (totalSales === 0) {
      return (
        <EmptyState
          icon="TrendingUp"
          title="Sin ventas registradas"
          description="Aún no has cerrado ninguna venta. Los márgenes y análisis aparecerán aquí cuando registres tu primera venta."
          ctaLabel="Ir a Inventario"
          ctaAction={onNavigateInventory}
          colSpan={COLUMNS?.length}
        />
      );
    }
    if (hasSearch) {
      return (
        <EmptyState
          icon="Filter"
          title="Sin resultados"
          description="Ninguna venta coincide con los filtros activos."
          ctaLabel="Limpiar filtros"
          ctaAction={() => { setSearch(''); setDateRange(30); }}
          colSpan={COLUMNS?.length}
        />
      );
    }
    if (hasDateFilter) {
      return (
        <EmptyState
          icon="Calendar"
          title="Sin ventas en este período"
          description="No hay ventas registradas en el rango de fechas seleccionado. Prueba ampliar el período."
          ctaLabel="Ver todos los períodos"
          ctaAction={() => setDateRange(null)}
          colSpan={COLUMNS?.length}
        />
      );
    }
    return (
      <EmptyState
        icon="Filter"
        title="Sin resultados"
        description="Ninguna venta coincide con los filtros activos."
        ctaLabel="Limpiar filtros"
        ctaAction={() => { setSearch(''); setDateRange(30); }}
        colSpan={COLUMNS?.length}
      />
    );
  };

  const FILTER_LABELS = { metal: 'Metal', category: 'Categoría', buyer: 'Comprador' };

  return (
    <>
      <div className="bg-card border border-border rounded-xl" style={{ boxShadow: 'var(--shadow-sm)' }}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="Table" size={16} color="var(--color-accent)" strokeWidth={2} />
            <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Registro de Ventas</h2>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{filtered?.length}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Date range */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              {DATE_RANGES?.map(dr => (
                <button
                  key={dr?.label}
                  onClick={() => setDateRange(dr?.days)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    dateRange === dr?.days
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
                >
                  {dr?.label}
                </button>
              ))}
            </div>
            {/* Search */}
            <div className="relative">
              <Icon name="Search" size={13} color="var(--color-muted-foreground)" className="absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                placeholder="Metal, categoría, comprador..."
                className="pl-8 pr-3 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent w-48"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              />
            </div>
            {/* Export */}
            <button
              onClick={() => exportCSV(filtered)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80 transition-colors"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              <Icon name="Download" size={13} color="currentColor" />
              CSV
            </button>
          </div>
        </div>

        {/* Active drill-down filter chips */}
        {activeExternalFilters?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-border bg-accent/5">
            <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Filtros activos:</span>
            {activeExternalFilters?.map(([key, val]) => (
              <span
                key={key}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border"
                style={{ background: 'var(--color-accent)', color: '#fff', borderColor: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                <span className="opacity-75">{FILTER_LABELS?.[key]}:</span>
                {val}
                <button
                  onClick={() => onClearExternalFilter?.(key)}
                  className="ml-0.5 hover:opacity-75 transition-opacity"
                  aria-label={`Quitar filtro ${key}`}
                >
                  <Icon name="X" size={11} color="#fff" />
                </button>
              </span>
            ))}
            <button
              onClick={() => { Object.keys(externalFilters || {})?.forEach(k => onClearExternalFilter?.(k)); }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              Limpiar todos
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            <thead>
              <tr className="border-b border-border">
                {COLUMNS?.map(col => (
                  <th
                    key={col?.key}
                    onClick={() => col?.sortable && handleSort(col?.key)}
                    className={`px-3 py-2.5 text-left text-muted-foreground font-medium whitespace-nowrap ${
                      col?.sortable ? 'cursor-pointer hover:text-foreground select-none' : ''
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      {col?.label}
                      <SortIcon col={col} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 })?.map((_, i) => (
                  <tr key={i} className="border-b border-border/50">
                    {COLUMNS?.map(col => (
                      <td key={col?.key} className="px-3 py-2.5">
                        <div className="h-3.5 bg-muted rounded animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered?.length === 0 ? (
                getEmptyState()
              ) : (
                filtered?.map((sale, idx) => {
                  const marginPct = sale?.margin_pct;
                  const hasBatch = !!sale?.batch_id;
                  return (
                    <tr key={sale?.id || idx} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      {/* Batch chip + Ver lote button */}
                      <td className="px-3 py-2.5">
                        {hasBatch ? (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => setTimelineModal({ batchId: sale?.batch_id })}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors hover:opacity-80"
                              style={{ background: 'var(--color-accent)/10', borderColor: 'var(--color-accent)', color: 'var(--color-accent)', fontFamily: '"IBM Plex Mono", monospace', maxWidth: '120px' }}
                              title="Ver historial del lote"
                            >
                              <Icon name="Layers" size={10} color="var(--color-accent)" />
                              <span className="truncate">
                                {sale?.metal_name ? `${sale?.metal_name?.slice(0,2)?.toUpperCase()}` : ''}
                                {sale?.category_name ? `·${sale?.category_name?.slice(0,4)}` : ''}
                                {sale?.weight_t ? `·${parseFloat(sale?.weight_t)?.toFixed(1)}t` : ''}
                              </span>
                            </button>
                            <button
                              onClick={() => setTimelineModal({ batchId: sale?.batch_id })}
                              className="p-1 rounded hover:bg-muted transition-colors"
                              title="Ver historial del lote"
                            >
                              <Icon name="Eye" size={13} color="var(--color-accent)" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                        {sale?.sold_at ? new Date(sale?.sold_at)?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                      </td>
                      <td className="px-3 py-2.5 font-medium text-foreground">{sale?.metal_name || '—'}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{sale?.category_name || '—'}</td>
                      <td className="px-3 py-2.5 font-mono text-right">{parseFloat(sale?.weight_t)?.toFixed(2)}</td>
                      <td className="px-3 py-2.5 font-mono text-right">{sale?.entry_price > 0 ? sale?.entry_price?.toLocaleString('es-ES', { maximumFractionDigits: 0 }) : '—'}</td>
                      <td className="px-3 py-2.5 font-mono text-right">{parseFloat(sale?.sale_price_per_ton)?.toLocaleString('es-ES', { maximumFractionDigits: 0 })}</td>
                      <td className={`px-3 py-2.5 font-mono font-semibold text-right ${getMarginColor(marginPct)}`}>
                        <span className={`px-1.5 py-0.5 rounded ${getMarginBg(marginPct)}`}>
                          {sale?.margin_per_ton > 0 ? '+' : ''}{sale?.margin_per_ton?.toLocaleString('es-ES', { maximumFractionDigits: 0 })}
                        </span>
                      </td>
                      <td className={`px-3 py-2.5 font-mono font-semibold text-right ${getMarginColor(marginPct)}`}>
                        {parseFloat(sale?.realized_pnl) > 0 ? '+' : ''}{parseFloat(sale?.realized_pnl)?.toLocaleString('es-ES', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-right">
                        {sale?.yield_est ? `${sale?.yield_est}%` : '—'} / {sale?.actual_yield_pct ? `${parseFloat(sale?.actual_yield_pct)?.toFixed(1)}%` : '—'}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-right text-muted-foreground">{sale?.days_in_inventory ?? '—'}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{sale?.buyer_name || '—'}</td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          Vendido
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* BatchTimelineModal */}
      {timelineModal && (
        <BatchTimelineModal
          batchId={timelineModal?.batchId}
          onClose={() => setTimelineModal(null)}
        />
      )}
    </>
  );
}
